import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { GetOrganizationCompetenciesQuery } from "types/graphql-schema";

import { keepCriteriaEditsSince, savedCriteriaByCompetencyId } from "./helpers";
import { CompetencyCriteriaInputWithUUID } from "./types";
import usePendingDeletes from "./use-pending-deletes";

// Unsaved criteria edits, by competency id, kept in step with the
// competencies query. When a refetch brings a change made elsewhere
// (Topicflow AI reworded a criterion, say), the edits take it, except where
// edited here since. Otherwise the next save sends the old wording back over
// the change.
const useKeepCriteriaEditsSince = ({
  data,
  setEdits,
  pendingDeletes,
  deleteList,
}: {
  data?: GetOrganizationCompetenciesQuery;
  setEdits: Dispatch<
    SetStateAction<Record<number, CompetencyCriteriaInputWithUUID[]>>
  >;
  pendingDeletes: ReturnType<typeof usePendingDeletes>;
  // The pending-deletes list of a competency's criteria: "7" or "shared-7".
  deleteList: "" | "shared-";
}) => {
  // The saved criteria the edits were made against.
  const lastLoadedRef = useRef<ReturnType<
    typeof savedCriteriaByCompetencyId
  > | null>(null);
  useEffect(() => {
    if (!data) {
      return;
    }
    const saved = savedCriteriaByCompetencyId(data);
    const lastLoaded = lastLoadedRef.current;
    lastLoadedRef.current = saved;
    if (!lastLoaded) {
      return;
    }
    setEdits((edits) =>
      keepCriteriaEditsSince(
        edits,
        saved,
        lastLoaded,
        pendingDeletes.savedIds(),
        (competencyId) => pendingDeletes.get(`${deleteList}${competencyId}`),
      ),
    );
  }, [data, deleteList, pendingDeletes, setEdits]);
};

export default useKeepCriteriaEditsSince;
